/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

let runtimeReferentials = [];

export function setRuntimeReferentials(rows = []) {
  runtimeReferentials = Array.isArray(rows) ? rows : [];
}

function runtimeRef(code, type = null) {
  if (!code) return null;
  return runtimeReferentials.find(r => r.code === code && r.active !== false && (!type || r.type === type)) || null;
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

// Normalize a date to noon UTC to avoid timezone day shift
export function toNoonUTC(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)).toISOString();
}

export function formatDateTime(date) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function formatTimeHundredths(value) {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(2)} j`;
}


// Status labels
export const statusLabels = {
  // Project
  draft: 'Brouillon',
  preparation: 'En préparation',
  in_progress: 'En cours',
  on_hold: 'En attente',
  closed: 'Clôturé',
  archived: 'Archivé',
  
  // Requirement
  to_draft: 'À rédiger',
  to_analyze: 'À analyser',
  to_validate: 'À valider',
  validated: 'Validée',
  rejected: 'Rejetée',
  obsolete: 'Obsolète',
  
  // Task
  todo: 'À faire',
  analysis: 'Analyse',
  development: 'Développement',
  deliverable: 'Livrable',
  testing: 'Recette',
  production: 'Production',
  
  // Sprint
  planned: 'Planifié',
  active: 'Actif',
  completed: 'Terminé',
  
  // Milestone
  pending: 'En attente',
  achieved: 'Atteint',
  delayed: 'En retard',
  
  // Test
  passed: 'Réussi',
  failed: 'Échoué',
  blocked: 'Bloqué',
  not_executed: 'Non exécuté',
  to_review: 'À contrôler',
};

// Status colors
export const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  preparation: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-emerald-100 text-emerald-700',
  on_hold: 'bg-amber-100 text-amber-700',
  closed: 'bg-blue-100 text-blue-700',
  archived: 'bg-slate-100 text-slate-500',
  
  to_draft: 'bg-slate-100 text-slate-700',
  to_analyze: 'bg-sky-100 text-sky-700',
  to_validate: 'bg-amber-100 text-amber-700',
  validated: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  obsolete: 'bg-slate-100 text-slate-500',
  
  todo: 'bg-slate-100 text-slate-700',
  analysis: 'bg-sky-100 text-sky-700',
  development: 'bg-violet-100 text-violet-700',
  deliverable: 'bg-orange-100 text-orange-700',
  testing: 'bg-amber-100 text-amber-700',
  production: 'bg-emerald-100 text-emerald-700',
  
  planned: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  
  pending: 'bg-amber-100 text-amber-700',
  achieved: 'bg-emerald-100 text-emerald-700',
  delayed: 'bg-red-100 text-red-700',
  
  passed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  blocked: 'bg-amber-100 text-amber-700',
  not_executed: 'bg-slate-100 text-slate-700',
  to_review: 'bg-orange-100 text-orange-700',
};

// Priority
export const priorityLabels = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  critical: 'Critique',
};

export const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

// Requirement type
export const requirementTypeLabels = {
  business: 'Métier',
  functional: 'Fonctionnelle',
  technical: 'Technique',
};

export const requirementTypeColors = {
  business: 'bg-blue-100 text-blue-700',
  functional: 'bg-emerald-100 text-emerald-700',
  technical: 'bg-orange-100 text-orange-700',
};

// Role
export const roleLabels = {
  admin: 'Administrateur',
  supervisor: 'Superviseur',
  project_manager: 'Chef de projet',
  manager: 'Manager',
  developer: 'Developpeur',
  technician: 'Technicien IT',
  tester: 'Testeur / Qualite',
  business_analyst: 'Metier / MOA',
  director: 'Dirigeant',
  viewer: 'Observateur',
  client: 'Client',
};

// Criticality
export const criticalityLabels = {
  minor: 'Mineure',
  major: 'Majeure',
  blocking: 'Bloquante',
};

export const criticalityColors = {
  minor: 'bg-slate-100 text-slate-700',
  major: 'bg-orange-100 text-orange-700',
  blocking: 'bg-red-100 text-red-700',
};

export function getBadgeClass(value, type = 'status') {
  const colorMap = {
    status: statusColors,
    priority: priorityColors,
    type: requirementTypeColors,
    criticality: criticalityColors,
  };
  return colorMap[type]?.[value] || 'bg-slate-100 text-slate-700';
}

export function getLabel(value, type = 'status') {
  const rr = runtimeRef(value);
  if (rr?.label) return rr.label;
  const labelMap = {
    status: statusLabels,
    priority: priorityLabels,
    type: requirementTypeLabels,
    role: roleLabels,
    criticality: criticalityLabels,
  };
  return labelMap[type]?.[value] || value;
}

export function truncate(text, length = 50) {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
}

export function percentage(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

export function daysUntil(date) {
  if (!date) return null;
  const now = new Date();
  const target = new Date(date);
  const diff = target - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function isOverdue(date) {
  if (!date) return false;
  return new Date(date) < new Date();
}

export function getTaskStatusColor(status) {
  const rr = runtimeRef(status, 'task_status');
  if (rr?.color) return rr.color;
  const colors = {
    todo: '#64748b',
    analysis: '#0284c7',
    development: '#7c3aed',
    deliverable: '#ea580c',
    testing: '#eab308',
    production: '#16a34a',
    closed: '#059669',
  };
  return colors[status] || '#64748b';
}

export function calculateProgress(consumed, estimated) {
  if (!estimated || estimated === 0) return 0;
  return Math.min(Math.round((consumed / estimated) * 100), 100);
}

export function getProgressColor(progress) {
  if (progress >= 100) return 'text-red-600';
  if (progress >= 80) return 'text-orange-600';
  if (progress >= 50) return 'text-amber-600';
  return 'text-emerald-600';
}

export function getInitials(name) {
  return name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';
}
